/**
 * Скидочная карта гостя.
 *
 * Постоянный гость приходит с картой, кассир подносит её к сканеру —
 * скидка встаёт в чек сама. Сканер штрихкодов для компьютера работает
 * как клавиатура: печатает цифры и жмёт Enter. Поэтому отдельного
 * «режима сканирования» нет — есть поле, которое всегда готово принять
 * ввод, и оно же принимает набранный руками номер, если карта затёрлась.
 */
import { useEffect, useRef, useState } from 'react';
import { useKeyboard } from './useKeyboard';
import { BASE } from '../api';

type Verdict =
  | { ok: true; cardId: string; percent: number; label: string }
  | { ok: false; reason: string };

const REASON_RU: Record<string, string> = {
  NOT_FOUND: 'Карта не найдена. Проверьте номер',
  INACTIVE: 'Карта заблокирована',
  USED: 'Этот купон уже использован',
  EXPIRED: 'Срок карты истёк',
};

export function CardDialog({ token, onApply, onCancel }: {
  token: string;
  onApply: (v: { cardId: string; percent: number; label: string }) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState('');
  const kb = useKeyboard(code, setCode, 'number');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Курсор в поле сразу: сканер начнёт печатать в ту же секунду,
  // и кассиру не придётся сначала целиться в поле пальцем
  useEffect(() => { kb.props.ref.current?.focus(); }, []);

  const check = async (raw: string) => {
    const c = raw.trim();
    if (c.length < 3) return;
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${BASE}/cards/check?code=${encodeURIComponent(c)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const v = await res.json() as Verdict;
      if (!v.ok) {
        setErr(REASON_RU[v.reason] ?? 'Карта не подошла');
        setCode('');
        kb.props.ref.current?.focus();
        return;
      }
      onApply({ cardId: v.cardId, percent: v.percent, label: v.label });
    } catch {
      setErr('Нет связи с сервером — скидку можно поставить вручную');
    } finally { setBusy(false); }
  };

  return (
    <div className="dialog-backdrop" role="dialog" aria-modal="true" aria-label="Скидочная карта">
      <div className="dialog card-dialog">
        <span className="eyebrow">Скидка гостя</span>
        <h1>Скидочная карта</h1>
        <p className="hint">
          Поднесите карту к сканеру или наберите номер вручную.
          Карта остаётся у гостя, купон сгорит после оплаты.
        </p>

        <input
          className="card-code"
            {...kb.props}
          inputMode="numeric"
          value={code}
          placeholder="Номер карты"
          autoCapitalize="characters"
          spellCheck={false}
          onChange={(e) => setCode(e.target.value)}
          // Сканер заканчивает ввод переводом строки — ловим его
          // и проверяем карту, не заставляя кассира жать кнопку
          onKeyDown={(e) => { if (e.key === 'Enter') void check(code); }}
        />

        {err && <p className="error">{err}</p>}

        <div className="dialog-actions">
          <button className="btn ghost" onClick={onCancel} disabled={busy}>Отмена</button>
          <button className="btn accent" disabled={busy || code.trim().length < 3}
            onClick={() => void check(code)}>
            {busy ? 'Проверяем…' : 'Применить'}
          </button>
        </div>
      </div>
      {kb.node}
    </div>
  );
}
